import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, HelpCircle, Users, TrendingUp, ArrowRight, Clock } from 'lucide-react';
import { adminGetStats, adminGetLeads } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    services: 0,
    faqs: 0,
    total_leads: 0,
    new_leads: 0,
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, leadsRes] = await Promise.all([
          adminGetStats(),
          adminGetLeads()
        ]);
        setStats(statsRes.data);
        setRecentLeads(leadsRes.data.slice(0, 5));
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { 
      title: 'Shërbimet Aktive', 
      value: stats.services, 
      icon: Wrench, 
      color: 'bg-blue-500',
      link: '/admin/services' 
    },
    { 
      title: 'FAQ-të', 
      value: stats.faqs, 
      icon: HelpCircle, 
      color: 'bg-green-500',
      link: '/admin/faqs' 
    },
    { 
      title: 'Kërkesat Totale', 
      value: stats.total_leads, 
      icon: Users, 
      color: 'bg-purple-500',
      link: '/admin/leads' 
    },
    { 
      title: 'Kërkesat e Reja', 
      value: stats.new_leads, 
      icon: TrendingUp, 
      color: 'bg-[#D4B982]',
      link: '/admin/leads' 
    },
  ];

  const statusLabels = {
    'neu': { text: 'E Re', class: 'bg-[#D4B982] text-[#0A0A0A]' },
    'in_bearbeitung': { text: 'Në Proces', class: 'bg-blue-100 text-blue-800' },
    'erledigt': { text: 'E Përfunduar', class: 'bg-green-100 text-green-800' },
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#0A0A0A]">Paneli Kryesor</h1>
        <p className="text-[#71717A]">Mirë se vini në panelin e administrimit të A&D Service</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card) => (
          <Link
            key={card.title}
            to={card.link}
            className="bg-white p-6 rounded-sm border border-[#E4E4E7] hover:border-[#D4B982] transition-colors group"
            data-testid={`stat-card-${card.title.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${card.color} rounded-sm flex items-center justify-center`}>
                <card.icon className="text-white" size={24} />
              </div>
              <ArrowRight className="text-[#71717A] group-hover:text-[#D4B982] transition-colors" size={20} />
            </div>
            <p className="text-3xl font-bold text-[#0A0A0A] mb-1">{card.value}</p>
            <p className="text-sm text-[#71717A]">{card.title}</p>
          </Link>
        ))}
      </div>

      {/* Recent Leads */}
      <div className="bg-white rounded-sm border border-[#E4E4E7]">
        <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[#0A0A0A]">Kërkesat e Fundit</h2>
            <p className="text-sm text-[#71717A]">Kërkesat e reja nga klientët</p>
          </div>
          <Link 
            to="/admin/leads" 
            className="text-sm text-[#D4B982] hover:underline font-medium"
          >
            Shiko të gjitha →
          </Link>
        </div>

        {recentLeads.length > 0 ? (
          <div className="divide-y divide-[#E4E4E7]">
            {recentLeads.map((lead) => (
              <div key={lead.id} className="p-6 hover:bg-[#F4F4F5] transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-[#0A0A0A] truncate">{lead.name}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusLabels[lead.status]?.class || 'bg-gray-100'}`}>
                        {statusLabels[lead.status]?.text || lead.status}
                      </span>
                    </div>
                    <p className="text-sm text-[#71717A] mb-1">{lead.email}</p>
                    <p className="text-sm text-[#71717A] truncate">{lead.message}</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#71717A]">
                    <Clock size={14} />
                    {formatDateTime(lead.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Users className="mx-auto text-[#71717A] mb-4" size={48} />
            <p className="text-[#71717A]">Nuk ka kërkesa ende</p>
          </div>
        )}
      </div>
    </div>
  );
}
